/** Pânza pe care se desenează un tronson: încadrare, panoramare, mărire.
 *
 *  ACELEAȘI GESTURI CA VIZUALIZATORUL DE ARHITECTURĂ, și nu din simpatie: cine
 *  se plimbă printr-un plan de ansamblu și apoi deschide un șablon nu are de ce
 *  să învețe a doua oară cum se dă zoom. Deci se împrumută modelul lui de
 *  transformare (`viewer/view.ts`: `translate(tx ty) scale(k)`, k în pixeli pe
 *  metru), citirea rotiței (`scrollIntent` — pinch mărește, scroll mută) și
 *  tastele ținute apăsate ca într-un joc (W/A/S/D și săgețile mută, `0`
 *  reîncadrează).
 *
 *  `k` E ÎN PIXELI PE METRU, iar asta e ce face desenul să știe de sine: hașura
 *  unui perete dispare când liniile ei ajung mai dese de 2,5 px, eticheta unei
 *  camere nu se scrie sub 6,5 px înălțime. Cu un `viewBox` care se strânge,
 *  mărirea n-ar fi fost o valoare pe care desenul o poate citi.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { COMENZI_REVIT, INCEPUTURI, RASTIMP_COMANDA_MS } from "./editor/comenzi";
import { fitBbox, scrollIntent, K_MIN, type Insets, type View } from "../view";
import { cadrulDesenului } from "./doc";
import type { PlanTronson, Pt } from "./doc";
import { Icoana } from "./icoane";
import { useT } from "../../i18n";

/** Cât mută o apăsare de tastă, în pixeli pe cadru. */
const PAS_TASTA = 9;

/** DUPĂ CÂT TIMP O LITERĂ ȚINUTĂ APĂSAT ÎNCETEAZĂ SĂ MAI FIE ÎNCEPUT DE
 *  COMANDĂ și redevine tasta ei de panoramare.
 *
 *  O comandă din două litere se TASTEAZĂ: între cele două bătute dinadins trec
 *  80–250 ms. O treime de secundă e dincolo de orice asemenea pauză și destul
 *  de scurtă cât ținutul să pară imediat. Sub `RASTIMP_COMANDA_MS`, care e cu
 *  totul altceva: cât se mai AȘTEAPTĂ a doua literă după o apăsare scurtă. */
const TINUT_PAN_MS = 330;

/** CÂT DE APROAPE SE POATE INTRA PE UN TRONSON.
 *
 *  `K_MAX` din `viewer/view.ts` e 60 și rămâne 60: el e plafonul
 *  VIZUALIZATORULUI DE ANSAMBLU, unde se citește un cvartal întreg, iar
 *  `canonical.ts` are un prag calibrat pe el. Un tronson e însă un desen de
 *  detaliu — pe el se pun prize, se trag goluri, se aliniază fețe de zid —, iar
 *  la 60 px/m un centimetru are 0,6 px: nu se poate lucra fin.
 *
 *  720 px/m înseamnă 1 cm = 7,2 px, adică un milimetru are șapte zecimi de
 *  pixel: se vede nu doar unde cade fața unui perete, ci și pe ce parte a ei
 *  cade o priză lipită de un corp de bucătărie. Plafonul era 240 — 1 cm = 2,4
 *  px —, iar cerința e «de trei ori mai mult zoom»: la piesele mici,
 *  întrerupătorul lângă toc sau capătul unui parapet, 2,4 px pe centimetru e
 *  chiar mărimea la care mâna nu mai distinge ce apucă.
 *
 *  Plafonul e al pânzei de tronson și numai al ei, ca să nu se schimbe nimic
 *  în vizualizatorul de arhitectură. */
export const K_MAX_TRONSON = 720;

export type Ecran = { view: View; dim: { w: number; h: number } };

export function useIncadrare(
  plan: PlanTronson | null,
  gazda: React.RefObject<HTMLDivElement>,
  /** CINE E MODULUL, ca șir stabil — de obicei `doc.id`.
   *
   *  Reîncadrarea trebuie să se întâmple când se deschide ALT modul, și numai
   *  atunci. Fără cheia asta se lua după mărimile cadrului, iar mărimile alea
   *  sunt chiar ce schimbă cineva când trage de contur: desenul se reîncadra la
   *  fiecare `pointermove`, deci pânza sărea în zoom sub mână cât ținea gestul.
   *  Nota de deasupra spunea de mult ce trebuie («la schimbarea MODULULUI, nu la
   *  fiecare mișcare»); cheia era cea care n-o spunea. */
  cheieModul?: string,
  /** CE SE ÎNCADREAZĂ, când nu e doar planul: pe «Toate corpurile» masa ține
   *  un corp și le desenează pe celelalte în jurul lui, iar încadrarea
   *  trebuie să le cuprindă pe toate (v. `plan/EditorNivel`). */
  cadruTot?: { x: number; y: number; w: number; h: number } | null,
  /** PIXELII DE LA MARGINE PE CARE ÎNCADRAREA NU-I FOLOSEȘTE — pentru ce se
   *  desenează în afara conturului la o depărtare care NU se scalează.
   *
   *  Marginea lui `fitBbox` e în metri, deci crește și scade odată cu desenul;
   *  cifra unei cote stă la un număr fix de PIXELI dincolo de linia ei, oricât
   *  de mare ar fi modulul. Fără rezerva asta, aparatul axelor cade afară din
   *  pânză la orice mărire — v. `editor/Panza`, unde se dă. */
  margini?: Insets,
) {
  const [view, setView] = useState<View | null>(null);
  const [dim, setDim] = useState({ w: 0, h: 0 });
  /** A UMBLAT CINEVA LA VEDERE de la ultima încadrare? Mâna bate reîncadrarea:
   *  cine a mărit ca să vadă un colț nu vrea ca lățirea ferestrei să-i strice
   *  mărirea. Dar cât timp vederea e cea PUSĂ DE NOI, ea trebuie să rămână o
   *  încadrare — v. `useEffect`-ul de redimensionare. */
  const deMana = useRef(false);
  /** `setView` pentru MÂNĂ — panoramare, rotiță, butoanele de zoom. Tot ce
   *  trece pe-aici marchează vederea ca atinsă. */
  const pune = useCallback<typeof setView>((v) => {
    deMana.current = true;
    setView(v);
  }, []);

  /** MÂNA A ATINS PLANȘA — fără să fi mișcat vederea.
   *
   *  `deMana` se aprindea numai la panoramare, rotiță sau butoanele de zoom.
   *  Cine EDITEAZĂ fără să panorameze — alege un zid, îl trage, scrie o cotă —
   *  lăsa vederea «a noastră», iar atunci orice REDIMENSIONARE a pânzei o
   *  reîncadra: alegi un obiect, se deschide panoul din dreapta, pânza se
   *  îngustează, iar desenul sare înapoi la încadrare. «Sometimes when I work
   *  on a tronson it zooms into the wrong spot like it randomly switches
   *  view» (19.10.2026) — nu era întâmplător, era chiar panoul care se
   *  deschide sub mână.
   *
   *  Reîncadrarea la redimensionare rămâne pentru planșa NEATINSĂ, unde chiar
   *  ajută: un modul abia deschis se așază singur cât se mai mișcă panourile.
   *  Din clipa în care a pus cineva degetul pe el, vederea e a lui. */
  const atinge = useCallback(() => { deMana.current = true; }, []);

  const incadreaza = useCallback(() => {
    const el = gazda.current;
    if (!el || !plan) return;
    const w = el.clientWidth || 800;
    const h = el.clientHeight || 500;
    // TOT CE E DESENAT, nu doar anvelopa: o piesă trasă afară din ziduri
    // rămâne desenată, dar încadrată pe anvelopă ea cade în afara ecranului și
    // nici `0`/`Z` n-o mai aduc înapoi — adică «mi-a dispărut obiectul»
    // (v. `doc::cadrulDesenului`).
    const c = cadruTot ?? cadrulDesenului(plan);
    setDim({ w, h });
    // Marginea de 0,8 m lasă loc cotelor de ansamblu (20 px în afara
    // conturului, plus cifra) și atât: cerut «zoom implicit puțin mai mare».
    deMana.current = false;
    setView(fitBbox([c.x, c.y, c.x + c.w, c.y + c.h], w, h,
                    { padM: 0.8, maxK: 90, minK: 2, insets: margini }));
  }, [plan, gazda, cadruTot, margini]);

  // Se reîncadrează la schimbarea MODULULUI, nu la fiecare mișcare de perete:
  // altfel desenul ar sări sub mână la fiecare gest.
  //
  // `plan ? ... : ""` face ca prima încadrare să vină când SOSEȘTE planul: cheia
  // trece din gol în numele modulului, iar cine deschide unul îl vede încadrat.
  // Fără ramura asta, un modul care se încarcă după montare n-ar fi încadrat
  // niciodată, fiindcă numele lui era deja cheia de la bun început.
  const cheie = plan
    ? (cheieModul ?? `${plan.cadru.w}x${plan.cadru.h}x${plan.colt}`)
    : "";
  useEffect(() => { incadreaza(); }, [cheie]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = gazda.current;
    if (!el) return;
    const obs = new ResizeObserver(() => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setDim((d) => (d.w === w && d.h === h ? d : { w, h }));
      // PÂNZA ȘI-A SCHIMBAT MĂRIMEA: se reîncadrează, dar NUMAI dacă vederea e
      // tot cea pusă de noi.
      //
      // Înainte nu se reîncadra niciodată, iar desenul rămânea la scara de la
      // deschidere. Pe o pânză care se micșorează — fereastra trasă, paleta
      // desfăcută, panoul de proprietăți deschis — el ieșea atunci pe sub
      // margini, cu tot ce stă în afara conturului: cotele dintre axe ajungeau
      // sub panoul din dreapta, iar apăsarea pe ele nimerea panoul. Măsurat pe
      // M3 la 1100 × 760: pânza 606 px lățime, a treia cifră la x = 839, adică
      // sub `ASIDE.ob-ed-prop`.
      //
      // Mâna rămâne deasupra: cine a mărit sau a tras de pânză păstrează ce a
      // făcut, fiindcă `deMana` e atunci adevărat.
      if (!deMana.current) incadreaza();
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [gazda, incadreaza]);

  return { view, setView: pune, dim, incadreaza, atinge };
}

/** Rotița, tastele și panoramarea cu mouse-ul, legate la o pânză.
 *
 *  `poatePanorama` spune dacă apăsarea pe FOND panoramează. În editor nu: acolo
 *  butonul stâng pe fond desenează sau deselectează, iar panoramarea se face cu
 *  butonul din mijloc, cu dreapta sau ținând spațiu — convenția oricărui editor
 *  de desen. În vizualizator da, fiindcă acolo n-are ce altceva să facă.
 */
export function useGesturi({
  gazda, view, setView, dim, incadreaza, incadreazaAles, poatePanorama,
  obiectAles = false, kMin = K_MIN, kMax = K_MAX_TRONSON,
}: {
  gazda: React.RefObject<HTMLDivElement>;
  view: View | null;
  setView: (f: (v: View | null) => View | null) => void;
  dim: { w: number; h: number };
  incadreaza: () => void;
  /** `Z` DUCE PÂNZA PE CE E ALES — cerut de arhitect (22.10.2026):
   *  «shortcutul "Z" mi-ar plăcea să facă zoom pe obiectul selectat; dacă nu e
   *  nimic selectat să facă așa cum e acum, pe tot planul».
   *
   *  Vine de afară fiindcă aici nu există nici documentul, nici selecția:
   *  `Panza2D` știe numai vederea. Cine o dă răspunde și de cutia pe care o
   *  încadrează (v. `editor/Panza`). Lipsă — ori întoarsă `false`, adică «n-am
   *  ce încadra» —, `Z` face ce face `0`: tot planul. */
  incadreazaAles?: () => boolean;
  poatePanorama: boolean;
  /** CE E ALES ÎȚI IA W/A/S/D ȘI SĂGEȚILE. Cu o piesă sau un perete ales,
   *  tastele alea împing OBIECTUL din 5 în 5 cm (v. `pages/Editor`), nu pânza:
   *  cine ține ceva selectat și apasă „d" — sau săgeata dreapta, care pentru
   *  cele mai multe mâini e același lucru — vrea lucrul acolo, nu planul
   *  plimbat pe sub el. Q/E măresc mai departe, iar pânza rămâne la îndemână
   *  cu mijlocul, cu dreapta sau cu spațiul. */
  obiectAles?: boolean;
  /** Cât se poate depărta și apropia (px/m). Editorul de structură le lasă
   *  practic libere. */
  kMin?: number;
  kMax?: number;
}) {
  const taste = useRef(new Set<string>());
  const cadru = useRef(0);
  /** Cum se oprește panoramarea în curs: dezlegarea ascultătorilor ei. Gol
   *  când nu se trage de nimic. */
  const opreste = useRef<(() => void) | null>(null);
  /** O panoramare e în curs? V. `panoramaPornita`. */
  const panoramam = useRef(false);
  /** Prima literă a unei comenzi din două, dacă tocmai s-a apăsat — v. `jos`. */
  const inceputDeComanda = useRef<{ ch: string; la: number } | null>(null);
  /** Ceasul literei ȚINUTE apăsat: dacă nu vine a doua literă, litera își
   *  recapătă fapta ei de panoramare — v. `TINUT_PAN_MS`. */
  const ceasTinut = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [spatiu, setSpatiu] = useState(false);

  // ---- rotița: pinch mărește, scroll mută ------------------------------- #
  useEffect(() => {
    const el = gazda.current;
    if (!el) return;
    const la = (e: WheelEvent) => {
      // Fără asta, browserul dă zoom pe pagină la pinch și pleacă înapoi în
      // istoric la scroll orizontal.
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const px = e.clientX - r.left;
      const py = e.clientY - r.top;
      setView((v) => {
        if (!v) return v;
        if (scrollIntent(e) === "zoom") {
          const k = Math.min(kMax, Math.max(kMin, v.k * Math.exp(-e.deltaY * 0.0018)));
          // Punctul de sub cursor rămâne sub cursor — altfel mărirea „fuge".
          return { k, tx: px - (px - v.tx) * (k / v.k),
                   ty: py - (py - v.ty) * (k / v.k) };
        }
        return { ...v, tx: v.tx - e.deltaX, ty: v.ty - e.deltaY };
      });
    };
    el.addEventListener("wheel", la, { passive: false });
    return () => el.removeEventListener("wheel", la);
  }, [gazda, setView, kMin, kMax]);

  // ---- tastele, ținute apăsate ------------------------------------------ #
  useEffect(() => {
    const SEMN: Record<string, string> = {
      arrowleft: "a", arrowright: "d", arrowup: "w", arrowdown: "s",
    };
    const scrie = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable);
    };
    const misca = () => {
      const t = taste.current;
      if (t.size) {
        setView((v) => v && ({
          ...v,
          tx: v.tx + ((t.has("a") ? PAS_TASTA : 0) - (t.has("d") ? PAS_TASTA : 0)),
          ty: v.ty + ((t.has("w") ? PAS_TASTA : 0) - (t.has("s") ? PAS_TASTA : 0)),
          k: t.has("e") ? Math.min(kMax, v.k * 1.02)
            : t.has("q") ? Math.max(kMin, v.k / 1.02) : v.k,
        }));
        cadru.current = requestAnimationFrame(misca);
      } else {
        cadru.current = 0;
      }
    };
    const opresteCeasul = () => {
      if (ceasTinut.current) clearTimeout(ceasTinut.current);
      ceasTinut.current = null;
    };
    const jos = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || scrie(e.target)) return;
      // SHIFT E AL EDITORULUI. Fără scutirea asta, pânza ar fugi sub obiectul
      // împins.
      if (e.shiftKey) return;
      const b = e.key.toLowerCase();
      const g = SEMN[b] ?? b;
      if (b === " ") { setSpatiu(true); e.preventDefault(); return; }
      // V. `obiectAles`: cât ține selecția, cele patru direcții sunt ale
      // obiectului — și pe litere, și pe săgeți.
      // LITERELE UNEI COMENZI SUNT ALE COMENZII, nu ale pânzei.
      //
      // `Masa` ține prima literă a comenzilor din două (v. `comenzi.ts`) și o
      // oprește cu `preventDefault`. Numai că `preventDefault` NU oprește și
      // celelalte ascultătoare: ăsta stă tot pe `window`, deci `W` din `WA`
      // ajungea aici și pornea panoramarea, iar `A` o ducea în stânga — «când
      // folosesc shortcut-ul pentru pereți „wa" mă și mișcă în plan»
      // (26.10.2026). Nici ordinea legării nu ajută: `Panza2D` se leagă
      // ÎNAINTEA `Masei`, fiindcă efectele copiilor rulează primele, deci aici
      // `e.defaultPrevented` e încă fals.
      //
      // NU E NEVOIE DE NICIUN RASTIMP NOU, deși așa s-a cerut («poate să fie
      // în funcție de cât de repede apeși cele 2 taste?»): se știe DEJA, la
      // prima literă, că ea începe o comandă — chiar asta e `INCEPUTURI`.
      //
      // A DOUA LITERĂ SE OPREȘTE NUMAI DACĂ CHIAR ÎNCHEIE O COMANDĂ: `WA` e
      // una, `WS` nu e. Altfel, după un `W` apăsat din greșeală, pânza ar fi
      // rămas înțepenită o secundă și jumătate la orice altă tastă.
      //
      // NUMAI PE LITERE, nu și pe săgeți: ele panoramează la fel și nu sunt
      // ale niciunei comenzi, deci toate patru direcțiile rămân la îndemână —
      // se pierde doar drumul dublat prin `W` și `D`.
      const litera = !(b in SEMN) && /^[a-z]$/.test(b) ? b.toUpperCase() : null;
      const asteptat = inceputDeComanda.current;
      const acum = Date.now();
      const incepe = !!litera && INCEPUTURI.has(litera);
      const incheie = !!litera && !!asteptat
        && acum - asteptat.la <= RASTIMP_COMANDA_MS
        && !!COMENZI_REVIT[asteptat.ch + litera];
      inceputDeComanda.current = incepe ? { ch: litera, la: acum } : null;
      if (incheie) { opresteCeasul(); return; }
      if (incepe) {
        // ȚINUTĂ APĂSAT, LITERA ÎȘI RECAPĂTĂ FAPTA EI. Cerut de arhitect
        // (27.10.2026): «deși e ok acum cum merge „wa", dacă țin apăsat lung pe
        // W nu mai are funcția izolată (nu mai mișcă). Dacă am putea păstra și
        // funcția izolată a lui W, adică după ce ai apăsat puțin, ar fi
        // perfect».
        //
        // O comandă se TASTEAZĂ, nu se ține apăsat: între două litere bătute
        // dinadins trec 80–250 ms. După `TINUT_PAN_MS` nu mai poate fi o
        // comandă, deci litera e a pânzei — iar comanda în așteptare se uită,
        // ca un `A` apăsat după aia să nu pornească `WA` peste panoramare.
        opresteCeasul();
        e.preventDefault();
        if ("wasdqe".includes(g) && g.length === 1
            && !(obiectAles && "wasd".includes(g))) {
          ceasTinut.current = setTimeout(() => {
            inceputDeComanda.current = null;
            taste.current.add(g);
            if (!cadru.current) cadru.current = requestAnimationFrame(misca);
          }, TINUT_PAN_MS);
        }
        return;
      }
      opresteCeasul();

      if (obiectAles && g.length === 1 && "wasd".includes(g)) return;
      if ("wasdqe".includes(g) && g.length === 1) {
        e.preventDefault();
        taste.current.add(g);
        if (!cadru.current) cadru.current = requestAnimationFrame(misca);
      } else if (b === "0") {
        e.preventDefault();
        incadreaza();
      } else if (b === "z") {
        // `Z` PE CE E ALES, și pe tot planul când nu e nimic ales. Cele două
        // sunt un singur gest — «du-mă unde lucrez» —, deci o singură tastă.
        // `0` rămâne întotdeauna tot planul: cine vrea ieșirea completă n-are
        // de ce s-o piardă fiindcă a uitat ceva selectat.
        e.preventDefault();
        if (!incadreazaAles?.()) incadreaza();
      }
    };
    const sus = (e: KeyboardEvent) => {
      // Ridicată înainte de ceas, litera rămâne doar începutul unei comenzi:
      // o apăsare scurtă nu panoramează.
      opresteCeasul();
      if (e.key === " ") setSpatiu(false);
      taste.current.delete(SEMN[e.key.toLowerCase()] ?? e.key.toLowerCase());
    };
    // Fereastra care pierde focusul nu mai trimite `keyup`, iar tasta ar rămâne
    // „apăsată" la nesfârșit.
    const goleste = () => {
      opresteCeasul(); taste.current.clear(); setSpatiu(false);
    };
    // APĂSAREA PE PÂNZĂ ÎI DĂ TASTATURA ÎNAPOI PÂNZEI.
    //
    // Scurtăturile pânzei sar peste orice tastă căzută într-un câmp de scris
    // (v. `scrie`), și bine fac: cine tastează «0» într-o cotă nu vrea să i se
    // reîncadreze desenul. Numai că focusul RĂMÂNE în câmp după ce omul se
    // întoarce la planșă — caseta unei cote, un câmp din panoul de
    // proprietăți —, iar atunci NICIO scurtătură nu mai merge: nici `Z`, nici
    // `0`, nici W/A/S/D, nici Delete. Arătat de arhitect (22.10.2026): «zoom-ul
    // pe obiecte e tot cu probleme», pe o înregistrare în care vederea nu se
    // clintește deloc, cu o casetă de cotă deschisă pe planșă.
    //
    // NUMAI CÂND APĂSAREA NU E CHIAR ÎNTR-UN CÂMP: altfel clicul în casetă și-ar
    // stinge singur focusul, și n-ai mai putea scrie în ea. Pe fază de CAPTURĂ,
    // ca să ajungă și la apăsările pe care desenul le oprește din urcat
    // (`stopPropagation` — v. zonele de apucat ale cotelor).
    const laPanza = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.("input, textarea, select")) return;
      const a = document.activeElement as HTMLElement | null;
      if (a && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName)) a.blur();
    };
    gazda.current?.addEventListener("pointerdown", laPanza, true);
    window.addEventListener("keydown", jos);
    window.addEventListener("keyup", sus);
    window.addEventListener("blur", goleste);
    return () => {
      gazda.current?.removeEventListener("pointerdown", laPanza, true);
      window.removeEventListener("keydown", jos);
      window.removeEventListener("keyup", sus);
      window.removeEventListener("blur", goleste);
      if (cadru.current) cancelAnimationFrame(cadru.current);
    };
  }, [gazda, setView, incadreaza, incadreazaAles, obiectAles, kMin, kMax]);

  /** Începe o panoramare. Se cheamă din `onPointerDown`-ul pânzei.
   *
   *  ASCULTĂTORII SE LEAGĂ AICI, LA APĂSARE, NU ÎNTR-UN `useEffect`.
   *
   *  Erau într-un efect fără listă de dependențe, care se uita la
   *  `panorama.current` și se oprea când îl găsea gol. Numai că `panorama` e un
   *  REF, scris în handlerul de `pointerdown`: scrierea lui nu cere o
   *  redesenare, deci efectul nu se mai rula niciodată după apăsare, iar
   *  `pointermove` rămânea nelegat. Panoramarea nu mișca nimic — nici cu
   *  mijlocul, nici cu dreapta, nici cu spațiu, nici în vizualizator, unde
   *  butonul stâng ar fi trebuit s-o pornească. Măsurat cu o probă: tras 100 px,
   *  `view.tx` rămânea unde era.
   *
   *  Legați la apăsare, ascultătorii trăiesc exact cât ține gestul și nu mai
   *  depind de ce se întâmplă între timp cu redesenarea.
   */
  const panoramaPornita = useCallback((
    /** Numai cât se citește: așa poate fi chemat și cu un `pointermove`, de pe
     *  drumul rotiței apăsate în timpul unei trageri (v. efectul de mai jos). */
    e: { button: number; clientX: number; clientY: number },
  ) => {
    if (!view) return false;
    const cuMouse = e.button === 1 || e.button === 2 || spatiu
      || (poatePanorama && e.button === 0);
    if (!cuMouse) return false;
    opreste.current?.();          // un gest rămas agățat, dacă a rămas vreunul
    const de = { sx: e.clientX, sy: e.clientY, tx: view.tx, ty: view.ty };
    const misca = (ev: PointerEvent) => {
      setView((v) => v && ({ ...v, tx: de.tx + (ev.clientX - de.sx),
                             ty: de.ty + (ev.clientY - de.sy) }));
    };
    const sus = () => {
      panoramam.current = false;
      window.removeEventListener("pointermove", misca);
      window.removeEventListener("pointerup", sus);
      // `pointercancel` vine când browserul ia gestul (o atingere devenită
      // scroll): fără el, ascultătorii ar rămâne legați și pânza ar continua să
      // se miște după ce degetul s-a ridicat.
      window.removeEventListener("pointercancel", sus);
      opreste.current = null;
    };
    panoramam.current = true;
    window.addEventListener("pointermove", misca);
    window.addEventListener("pointerup", sus);
    window.addEventListener("pointercancel", sus);
    opreste.current = sus;
    return true;
  }, [view, spatiu, poatePanorama, setView]);


  /** ROTIȚA APĂSATĂ ÎN TIMPUL UNEI TRAGERI NU DĂ `pointerdown`.
   *
   *  Asta nu e un capriciu al unui browser, e CHIAR SPECIFICAȚIA: la o
   *  „chorded button interaction" — un al doilea buton apăsat cât timp primul
   *  e jos — se trimite un `pointermove` cu `button` = butonul care s-a
   *  schimbat și `buttons` adus la zi, NU încă un `pointerdown`. Acela se
   *  trimite o singură dată, la trecerea de la „niciun buton" la „un buton".
   *
   *  Panoramarea pornea numai din `pointerdown` (v. `Panza::laFond`), deci cât
   *  ținea o tragere rotița nu mai putea porni nimic: «cât timp am left-click
   *  apăsat și sunt în timpul mutării să pot să apăs și scroll wheel și să facă
   *  pan» (26.10.2026). Singură, fără nimic în mână, mergea — fiindcă atunci
   *  apăsarea chiar e primul buton.
   *
   *  SE CITEȘTE `buttons`, NU `button`: masca spune ce e APĂSAT ACUM (bitul 2,
   *  adică 4, e rotița), pe când `button` spune doar ce s-a schimbat la
   *  evenimentul ăsta și e −1 pe mișcările următoare.
   *
   *  ÎN FAZA DE CAPTARE, ca semnul să fie pus ÎNAINTEA gestului: ascultătorul
   *  gestului stă pe pânză, iar `window` în bulă ar veni ultimul — gestul ar
   *  apuca să ducă obiectul cu toată mișcarea aia înainte să afle că se
   *  panoramează (v. `Panza::laMiscare`, care se oprește pe `panoramam`).
   *
   *  ȘI RIDICAREA ROTIȚEI tot un `pointermove` e, din același motiv: fără
   *  capătul ăsta, panoramarea ar ține până la ridicarea butonului STÂNG, iar
   *  restul tragerii ar plimba pânza în loc de obiect. */
  useEffect(() => {
    const la = (ev: PointerEvent) => {
      const rotita = (ev.buttons & 4) !== 0;
      if (rotita && !panoramam.current) panoramaPornita({
        button: 1, clientX: ev.clientX, clientY: ev.clientY });
      // Numai cu STÂNGA încă jos: altfel e o panoramare obișnuită, care se
      // încheie din `pointerup`, ca până acum.
      else if (!rotita && panoramam.current && (ev.buttons & 1) !== 0) {
        opreste.current?.();
      }
    };
    window.addEventListener("pointermove", la, true);
    return () => window.removeEventListener("pointermove", la, true);
  }, [panoramaPornita]);

  // Pânza care dispare în timpul unei trageri și-ar lăsa ascultătorii pe
  // `window` — v. `opreste`.
  useEffect(() => () => opreste.current?.(), []);

  return { panoramaPornita, spatiu, dim, panoramam };
}

/** Punctul din LUME de sub cursor, pentru un `view` dat. */
export function laLume(e: { clientX: number; clientY: number },
                       el: HTMLElement, view: View): Pt {
  const r = el.getBoundingClientRect();
  return [(e.clientX - r.left - view.tx) / view.k,
          (e.clientY - r.top - view.ty) / view.k];
}

/** CURSORUL DE MĂRIRE E LOGARITMIC, iar asta nu e o subtilitate de grafică: pe
 *  o riglă liniară de la 4 la 720 px/m, tot ce se folosește ca să se vadă un
 *  modul întreg — 25–90 px/m — ar sta în prima optime a ei, adică într-o
 *  fâșie de câțiva pixeli, iar restul cursorului ar fi măriri de detaliu prin
 *  care nimeni nu trece treptat. Pe una logaritmică fiecare bucată egală de
 *  drum e același RAPORT de mărire, deci pasul simțit e același pe tot cursorul
 *  — ca la rotiță, care înmulțește și ea. */
const TREPTE = 1000;
const kDinCursor = (t: number) =>
  K_MIN * (K_MAX_TRONSON / K_MIN) ** (t / TREPTE);
const cursorDinK = (k: number) =>
  Math.round(TREPTE * Math.log(k / K_MIN) / Math.log(K_MAX_TRONSON / K_MIN));

/** Bara de mărire, aceeași în vizualizator și în editor. */
export function BaraZoom({ view, setView, incadreaza }: {
  view: View | null;
  setView: (f: (v: View | null) => View | null) => void;
  incadreaza: () => void;
}) {
  const t = useT();
  const k = view?.k ?? K_MIN;
  const pune = (nou: number) => setView((v) => v && ({ ...v, k: nou }));
  return (
    <div className="ob-tr-zoom">
      <button title={t("panza.departeaza")}
              onClick={() => pune(Math.max(K_MIN, k / 1.25))}>
        <Icoana nume="minus" marime={14} />
      </button>
      <input type="range" min={0} max={TREPTE} step={1} value={cursorDinK(k)}
             onChange={(e) => pune(kDinCursor(Number(e.target.value)))} />
      <button title={t("panza.apropie")}
              onClick={() => pune(Math.min(K_MAX_TRONSON, k * 1.25))}>
        <Icoana nume="plus" marime={14} />
      </button>
      <button title={t("panza.incadreaza")} onClick={incadreaza}>
        <Icoana nume="incadreaza" marime={14} />
      </button>
      <span className="ob-tr-scara">{Math.round(k)} px/m</span>
    </div>
  );
}

/** REPERUL X–Y: încotro cresc coordonatele planșei.
 *
 *  Pânza nu se rotește niciodată — transformarea ei e `translate` + `scale` —,
 *  deci +X e mereu spre dreapta și +Y mereu în jos. Numai că «mereu» e o
 *  certitudine pe care o are codul, nu omul de la desen: cotele se citesc pe
 *  direcții, axele de trasare poartă litere pe una și cifre pe cealaltă, iar
 *  ce se împinge din taste pleacă pe una dintre ele. Un semn în colț face din
 *  convenția asta ceva ce se vede, nu ceva de ținut minte.
 *
 *  +Y E DESENAT ÎN JOS fiindcă acolo chiar crește. Un reper cu Y în sus ar
 *  arăta ca la școală și ar minți despre semnul oricărei deplasări: cine mută
 *  un zid cu 0,20 m pe Y trebuie să-l vadă coborând.
 *
 *  ÎN COLȚUL DIN STÂNGA JOS, cel mai gol colț al unei planșe: sus stă
 *  comutatorul de vederi și bara selecției, jos la mijloc bara de mărire.
 */
export function ReperXY() {
  const t = useT();
  return (
    <div className="ob-tr-reper" title={t("panza.reper_xy")}>
      <svg viewBox="0 0 58 58" width={46} height={46} aria-hidden="true">
        <g className="ob-tr-reper-linii">
          <line x1={14} y1={14} x2={40} y2={14} />
          <line x1={14} y1={14} x2={14} y2={40} />
          <circle cx={14} cy={14} r={2.1} />
        </g>
        <g className="ob-tr-reper-varf">
          <polygon points="39,10.6 46,14 39,17.4" />
          <polygon points="10.6,39 14,46 17.4,39" />
        </g>
        <text className="ob-tr-reper-nume" x={51} y={18}>X</text>
        <text className="ob-tr-reper-nume" x={14} y={56}>Y</text>
      </svg>
    </div>
  );
}

/** Cadrul de încadrare al planului, pentru cine vrea să reîncadreze pe altceva. */
export const cadrulPlanului = (plan: PlanTronson): [number, number, number, number] =>
  [plan.cadru.x, plan.cadru.y, plan.cadru.x + plan.cadru.w,
   plan.cadru.y + plan.cadru.h];

export const useMemoPlan = useMemo;
